import { Box } from '@material-ui/core'
import { useCallback, useState } from 'react'
import { Colors, SelectBox, ToggleSwitch, Typography, Checkbox } from 'src/components/atoms'
import { LayoutTeam } from 'src/components/commons'
import { ModalConfirmToPaidPlan } from 'src/components/modals'
import { Header } from 'src/components/organisms'
import {
  useGetOrganization,
  useGetOrganizationTriggerOptionList,
  useUpdateTriggerEmail
} from 'src/fixtures/modules/organization/hooks'
import { useTheme } from '@material-ui/core/styles'
import { useGetOrganizationPaymentCurrentPlan } from 'src/fixtures/modules/organizationPayment/hooks'
import { useFeatureFlag } from 'src/fixtures/modules/user/hooks'
import { useGlobalStyles } from 'src/styles/theme'

const MAX_REPEATED_RESENDING_COUNT_LIST = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 }
]

export const PageTeamTriggerMail = () => {
  const theme = useTheme()
  const globalClasses = useGlobalStyles()
  const [openModalUpgrade, setOpenModalUpgrade] = useState(false)
  const { hasPermission: isUpdatable } = useFeatureFlag('triggerEmail', 'update')
  const { data: organizationRes } = useGetOrganization()
  const isTriggerResentEmail = organizationRes?.organization?.isTriggerResentEmail
  const triggerResentTiming = organizationRes?.organization?.triggerResentTiming
  const { TriggerOptionList, TriggerRepeatOptionList } = useGetOrganizationTriggerOptionList()
  const isRepeatResending = organizationRes?.organization?.isRepeatResending
  const repeatedResendingTiming = organizationRes?.organization?.repeatedResendingTiming
  const maxRepeatedResendingCount = organizationRes?.organization?.maxRepeatedResendingCount
  const isWeekdayResending = organizationRes?.organization?.isWeekdayResending
  const { data: planGrade } = useGetOrganizationPaymentCurrentPlan()
  const { handleUpdateTriggerEmail } = useUpdateTriggerEmail()

  const handleChangeToggle = useCallback(() => {
    if (!planGrade?.plan?.canUseTriggerEmail) {
      setOpenModalUpgrade(true)
      return
    }
    handleUpdateTriggerEmail({
      organization: { isTriggerResentEmail: !isTriggerResentEmail }
    })
  }, [handleUpdateTriggerEmail, isTriggerResentEmail, planGrade?.plan?.canUseTriggerEmail])

  const handleChangeToggleRepeat = useCallback(() => {
    if (!planGrade?.plan?.canUseTriggerEmail) {
      setOpenModalUpgrade(true)
      return
    }
    handleUpdateTriggerEmail({
      organization: { isRepeatResending: !isRepeatResending }
    })
  }, [handleUpdateTriggerEmail, isRepeatResending, planGrade?.plan?.canUseTriggerEmail])

  const handleChangeResentEmailRepeatTiming = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      if (event.target.value === '') return
      handleUpdateTriggerEmail({
        organization: { isRepeatResending: true, repeatedResendingTiming: event.target.value as string }
      })
    },
    [handleUpdateTriggerEmail]
  )

  const handleChangeTriggerResentTiming = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      if (event.target.value === '') return
      handleUpdateTriggerEmail({
        organization: { isTriggerResentEmail: true, triggerResentTiming: event.target.value as string }
      })
    },
    [handleUpdateTriggerEmail]
  )

  const handleChangeMaxResentEmailCount = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      if (event.target.value === '') return
      handleUpdateTriggerEmail({
        organization: { isRepeatResending: true, maxRepeatedResendingCount: Number(event.target.value) }
      })
    },
    [handleUpdateTriggerEmail]
  )

  const handleChangeIsWeekdayResentEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleUpdateTriggerEmail({
        organization: { isRepeatResending: true, isWeekdayResending: event.target.checked }
      })
    },
    [handleUpdateTriggerEmail]
  )

  return (
    <>
      <LayoutTeam>
        <Box width={1} bgcolor={Colors.functional.background.default} px="16px" color={Colors.base.black}>
          <Header svgIcon="rocket" title="トリガーメール" back={false} textSize="large" iconSize="32px" />
          <Box px="16px">
            <Typography fontSize="s" letterSpacing="tight" fontWeight="normal" lineheight="23.8px">
              顧客に送付した資料が未開封の場合、設定した時間後に資料の開封を促すメールを自動送信します
            </Typography>
            <Box maxWidth="950px" display="flex" justifyContent="space-between" mt="40px">
              <Box>
                <Box width="320px">
                  <Box width="260px" display="flex" alignItems="center" mb="28px">
                    <Typography fontSize="s" letterSpacing="tight" fontWeight="bold" lineheight="14px">
                      再送する
                    </Typography>
                    <Box mr="8px" />
                    <ToggleSwitch
                      checked={!!isTriggerResentEmail}
                      onChange={handleChangeToggle}
                      disabled={!isUpdatable}
                    />
                  </Box>
                  <Typography fontSize="s" letterSpacing="tight" fontWeight="bold" lineheight="14px">
                    メールの再送タイミングを選択
                  </Typography>
                  <Box mb="12px" />
                  <SelectBox
                    size="medium"
                    placeholder="選択してください"
                    value={triggerResentTiming || ''}
                    optionList={TriggerOptionList || []}
                    onChange={handleChangeTriggerResentTiming}
                    disabled={!isUpdatable}
                  />
                </Box>
                <Box mb="36px" />
                {isTriggerResentEmail && (
                  <>
                    <Box width="260px" display="flex" alignItems="center" mb="12px">
                      <Typography fontSize="s" letterSpacing="tight" fontWeight="bold" lineheight="14px">
                        繰り返す
                      </Typography>
                      <Box mr="8px" />
                      <ToggleSwitch
                        checked={!!isRepeatResending}
                        onChange={handleChangeToggleRepeat}
                        disabled={!isUpdatable}
                      />
                    </Box>
                    {isRepeatResending && (
                      <Box>
                        <Box display="flex" alignItems="center">
                          <SelectBox
                            size="medium"
                            placeholder="選択してください"
                            value={repeatedResendingTiming || ''}
                            optionList={TriggerRepeatOptionList || []}
                            onChange={handleChangeResentEmailRepeatTiming}
                            disabled={!isUpdatable}
                          />
                          <Typography fontSize="s" style={{ marginLeft: '8px', marginRight: '8px' }}>
                            に
                          </Typography>
                          <Box style={{ width: '60px', marginRight: '8px' }}>
                            <SelectBox
                              size="medium"
                              placeholder="選択してください"
                              value={maxRepeatedResendingCount || ''}
                              optionList={MAX_REPEATED_RESENDING_COUNT_LIST}
                              onChange={handleChangeMaxResentEmailCount}
                              disabled={!isUpdatable}
                            />
                          </Box>
                          <Typography fontSize="s" style={{ whiteSpace: 'nowrap' }}>
                            回まで
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" mb="8px">
                          <Typography fontSize="s">平日のみ送信する</Typography>
                          <Checkbox checked={isWeekdayResending} onChange={handleChangeIsWeekdayResentEmail} />
                        </Box>
                        <Typography fontSize="s" style={{ color: theme.palette.text.secondary }}>
                          ※ 一度リンクを開いた顧客には繰り返しメールは送信されません
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <Box height="260px">
                <img src="/images/team/trigger-mail.png" width="100%" className={globalClasses.imgContain} />
              </Box>
            </Box>
          </Box>
        </Box>
      </LayoutTeam>

      <ModalConfirmToPaidPlan
        currentPlanGrade={planGrade?.plan.grade}
        open={openModalUpgrade}
        lightPlanText="ライトプランでは、こちらの機能はご利用いただけません。プレミアムプランなら、トリガーメール機能に加え、営業を強化するさまざまな機能がご利用いただけます"
        standardNewText="スタンダードプランでは、こちらの機能はご利用いただけません。プレミアムプランなら、トリガーメール機能に加え、営業を強化するさまざまな機能がご利用いただけます"
        onClose={() => setOpenModalUpgrade(false)}
      />
    </>
  )
}
